// file: components/chat/ConversationList.tsx
import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faReply } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import { useChatConnection } from "../../../contexts/ChatConnectionContext";
import {
  Conversation,
  GetMyChannelConversationAsync,
  Message,
  RemoveAdHocFromConversationDto,
  createConversation,
  deleteConversationAsync,
  getMyConversations,
  getAllConversations,
} from "../../../services/chatService/ChatService";
import { getAllUsersInOrganization } from "../../../services/userServices/UserInfoService";
import UserInfo from "../../../interfaces/UserInfo";
import plusicon from "../../../assets/images/plusicon.svg";
import chatgroupicon from "../../../assets/images/chatgroupicon.svg";
import ChannelImage from "../../../assets/images/CHAnnelImage.png";
import Form from "react-bootstrap/Form";
import { Button, Dropdown, Nav, Tab } from "react-bootstrap";
import { showConfirmationDialog } from "../../modal/ConfimationModal/Confimation";
import { conversationRead$ } from "../../../hooks/globalChatHooks/useGlobalChat";
import { toast } from "react-toastify";
import AvatarImage from "../../ui/Avatar/AvatarImage";
import { formatConversationTitle } from "../../../utilities/chat/conversationUtils";
import { useAuth } from "../../../firebase/AuthProvider";
import { MessageType } from "../../../Data/SD";
import { formatSystemMessage } from "../../../utilities/formatSystemMessage";
import { useNavigate } from "react-router-dom";
import CreateConversationModal from "./CreateConversationModal";
import { FiSearch } from "react-icons/fi";
import { GoPlus } from "react-icons/go";


const Search = FiSearch as any;
const Plus = GoPlus as any;


export interface ConversationListRef {
  toggleAddConversation: (anchorEl?: HTMLElement | null) => void;
}

interface ConversationListProps {
  authToken: string;
  userId: string;
  selectedConversationId: number | null;
  onSelectConversation: (conversation: Conversation) => void;
  width?: string;
  showHeader?: boolean;
  removeActiveMeetingFromSelectedConversation?: () => void;
  onCloseSideChat?: () => void;
  conversationRef?: React.RefObject<HTMLButtonElement>;
  handleConversation?: () => void;
  isSideView?: boolean;
}

/** Helpers */
const toTimestamp = (d: string | number | Date | null | undefined): number | null => {
  if (!d && d !== 0) return null;
  if (d instanceof Date) return d.getTime();
  if (typeof d === "number") return Number.isFinite(d) ? d : null;
  if (typeof d === "string") {
    const t = Date.parse(d);
    return Number.isFinite(t) ? t : null;
  }
  return null;
};

const normalizeMessage = (m: Message): Message & { _ts?: number } => {
  const ts = toTimestamp((m as any).createDate ?? (m as any).createdAt);
  return { ...m, _ts: ts ?? undefined };
};

const ConversationList = React.forwardRef<ConversationListRef, ConversationListProps>(
  (props, ref) => {
    const {
      authToken,
      userId,
      selectedConversationId,
      onSelectConversation,
      width,
      showHeader = true,
      removeActiveMeetingFromSelectedConversation,
      onCloseSideChat,
      conversationRef,
      handleConversation,
      isSideView = false,
    } = props;

    const { currentUser } = useAuth();
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [channelConversations, setChannelConversations] = useState<Conversation[]>([]);
    // New unified state for the combined inbox list [Channel and Normal Conversation Together]
    const [unifiedConversations, setUnifiedConversations] = useState<Conversation[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingUnified, setLoadingUnified] = useState<boolean>(false);
    const [users, setUsers] = useState<UserInfo[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [addConversationView, setAddConversationView] = useState<boolean>(false);
    const [searchTerm, setSearchTearm] = useState<string>("");
    const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({});
    const internalAddButtonRef = useRef<HTMLDivElement>(null);

    const [activeTab, setActiveTab] = useState<"Conversations" | "Channels">("Conversations");

    // New state for archived channels support
    const [showArchived, setShowArchived] = useState<boolean>(false);
    const [archivedPermissionError, setArchivedPermissionError] = useState<string | null>(null);
    const [useUnifiedEndpoint, setUseUnifiedEndpoint] = useState<boolean>(true); // Feature flag to switch between old and new

    // A map: conversationId -> array of userIds who are typing
    const [typingMap, setTypingMap] = useState<Record<number, string[]>>({});

    // Shared SignalR connection
    const { connection } = useChatConnection();

    const navigate = useNavigate();

    // Refs to avoid stale values in handlers
    const selectedConversationIdRef = useRef<number | null>(selectedConversationId);
    useEffect(() => {
      selectedConversationIdRef.current = selectedConversationId;
    }, [selectedConversationId]);

    // fetch initial lists
    useEffect(() => {
      if (authToken) {
        if (useUnifiedEndpoint) {
          getAllUnifiedConversations(authToken, showArchived);
        } else {
          // Legacy: keep existing behavior
          getMyConversation(authToken);
          getChannleConversation(authToken, userId);
        }
        AllUsersOfOrganization(authToken);
      }
    }, [authToken, userId, useUnifiedEndpoint, showArchived]);

    // external read events -> clear unread
    useEffect(() => {
      const subscription = conversationRead$.subscribe((readConversationId) => {
        const now = new Date();
        setConversations((prev) =>
          prev.map((convo) =>
            convo.id === readConversationId
              ? { ...convo, unreadCount: 0, lastReadAt: now }
              : convo
          )
        );
        setChannelConversations((prev) =>
          prev.map((convo) =>
            convo.id === readConversationId
              ? { ...convo, unreadCount: 0, lastReadAt: now }
              : convo
          )
        );
        if (useUnifiedEndpoint) {
          setUnifiedConversations((prev) =>
            prev.map((convo) =>
              convo.id === readConversationId
                ? { ...convo, unreadCount: 0, lastReadAt: now }
                : convo
            )
          );
        }
      });

      return () => subscription.unsubscribe();
    }, []);

    const getAllUnifiedConversations = async (token: string, includeArchived: boolean = false) => {
      setLoadingUnified(true);
      setArchivedPermissionError(null);

      try {
        const response = await getAllConversations(token, true, includeArchived);

        if (response.success && response.data) {
          setUnifiedConversations(response.data);
        } else {
          // Handle 403 Permission Denied for archived channels
          if (response.statusCode === 403) {
            setArchivedPermissionError(response.message || 'You don\'t have permission to view archived channels');
            // Fallback: fetch without archived if permission denied
            if (includeArchived) {
              const fallbackResponse = await getAllConversations(token, true, false);
              if (fallbackResponse.success && fallbackResponse.data) {
                setUnifiedConversations(fallbackResponse.data);
              }
              setShowArchived(false); // Reset toggle
            }
          } else {
            setError(response.message || 'Failed to load conversations');
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch unified conversations:', err);
        setError(err.message || 'Failed to load conversations');
      } finally {
        setLoadingUnified(false);
      }
    };

    const getChannleConversation = (token: string, uid: string) => {
      GetMyChannelConversationAsync(token, uid).then((res) => {
        if (res.success) {
          setChannelConversations(res.data || []);
        }
      });
    };

    const getMyConversation = (token: string) => {
      setLoading(true);
      getMyConversations(token).then((res) => {
        if (res) {
          setConversations(res);
        }
      }).finally(() => {
        setLoading(false);
      })
    };

    const AllUsersOfOrganization = (token: string) => {
      const needOnlyActive = true;
      getAllUsersInOrganization(token, needOnlyActive).then((res) => {
        if (res) {
          const userWithoutMe = res.filter(u => u.id != currentUser?.uid);
          setUsers(userWithoutMe);
        }
      });
    };

    // attach SignalR handlers once connection is ready
    useEffect(() => {
      if (!connection) return;

      // Placeholder for RemoveAdHocFromConversation event
      const handleRemoveAdHocFromConversation = (
        payload: RemoveAdHocFromConversationDto
      ) => {

        if (selectedConversationId == payload.conversationId) {
          if (removeActiveMeetingFromSelectedConversation) {
            removeActiveMeetingFromSelectedConversation();
          }
        }

        setConversations(prevConversations =>
          prevConversations.map(conversation =>
            conversation.id === payload.conversationId
              ? { ...conversation, activeMeeting: undefined }
              : conversation
          )
        );

        if (useUnifiedEndpoint) {
          setUnifiedConversations(prevConversations =>
            prevConversations.map(conversation =>
              conversation.id === payload.conversationId
                ? { ...conversation, activeMeeting: undefined }
                : conversation
            )
          );
        }
      };


      const handleTypingStatus = (
        convId: number,
        typingUserId: string,
        currentlyTyping: boolean
      ) => {
        if (typingUserId === userId) return;
        setTypingMap((prev) => {
          const currentList = prev[convId] || [];
          if (currentlyTyping) {
            if (!currentList.includes(typingUserId)) {
              return { ...prev, [convId]: [...currentList, typingUserId] };
            }
            return prev;
          } else {
            return {
              ...prev,
              [convId]: currentList.filter((id) => id !== typingUserId),
            };
          }
        });
      };

      /** NEW: bump unread for messages not authored by me & not in active convo */
      const handleReceiveMessage = (rawMessage: Message) => {
        const message = normalizeMessage(rawMessage);
        const msgTs = (message as any)._ts ?? toTimestamp((message as any).createDate) ?? 0;
        const isMine = message.author?.id === userId;
        const activeId = selectedConversationIdRef.current;

        // Update direct conversations
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === message.conversationId);
          if (idx === -1) return prev;

          const c = prev[idx];
          const shouldIncrement = !isMine && c.id !== activeId;
          const unreadCount = shouldIncrement ? (c.unreadCount ?? 0) + 1 : c.unreadCount ?? 0;

          const updated: Conversation = {
            ...c,
            lastMessage: message as any,
            unreadCount,
          };

          const without = prev.filter((x) => x.id !== c.id);
          return [updated, ...without];
        });

        // Also try to update channels (if any share id)
        setChannelConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === message.conversationId);
          if (idx === -1) return prev;

          const c = prev[idx];
          const shouldIncrement = !isMine && c.id !== activeId;
          const unreadCount = shouldIncrement ? (c.unreadCount ?? 0) + 1 : c.unreadCount ?? 0;

          const updated: Conversation = {
            ...c,
            lastMessage: message as any,
            unreadCount,
          };

          const without = prev.filter((x) => x.id !== c.id);
          return [updated, ...without];
        });

        if (useUnifiedEndpoint) {
          setUnifiedConversations((prev) => {
            const idx = prev.findIndex((c) => c.id === message.conversationId);
            if (idx === -1) return prev;

            const c = prev[idx];
            const shouldIncrement = !isMine && c.id !== activeId;
            const unreadCount = shouldIncrement ? (c.unreadCount ?? 0) + 1 : c.unreadCount ?? 0;

            const updated: Conversation = {
              ...c,
              lastMessage: message as any,
              unreadCount,
            };

            const without = prev.filter((x) => x.id !== c.id);
            return [updated, ...without];
          });
        }
      };

      /** Normalize and clear unread when *I* read */
      const handleReceiveReadReceipt = (
        convId: number,
        userIdWhoRead: string,
        lastReadAt: string | Date
      ) => {
        if (userIdWhoRead !== userId) return;
        const ts = toTimestamp(lastReadAt);
        const date = ts ? new Date(ts) : new Date();

        setConversations((prev) =>
          prev.map((c) =>
            c.id === convId ? { ...c, unreadCount: 0, lastReadAt: date } : c
          )
        );
        setChannelConversations((prev) =>
          prev.map((c) =>
            c.id === convId ? { ...c, unreadCount: 0, lastReadAt: date } : c
          )
        );

        if (useUnifiedEndpoint) {
          setUnifiedConversations((prev) =>
            prev.map((c) =>
              c.id === convId ? { ...c, unreadCount: 0, lastReadAt: date } : c
            )
          );
        }
      };

      const handleConversationCreated = (newConv: Conversation) => {
        setConversations((prev) => {
          const exists = prev.some((c) => c.id === newConv.id);
          if (exists) return prev;
          return [newConv, ...prev];
        });
        // Also add to unified list if using unified endpoint
        if (useUnifiedEndpoint) {
          setUnifiedConversations((prev) => {
            const exists = prev.some((c) => c.id === newConv.id);
            if (exists) return prev;
            return [newConv, ...prev];
          });
        }
      };

      connection.on("ReceiveTypingStatus", handleTypingStatus);
      connection.on("ReceiveMessage", handleReceiveMessage);
      connection.on("ReceiveReadReceipt", handleReceiveReadReceipt);
      connection.on("ConversationCreated", handleConversationCreated);

      // Register RemoveAdHocFromConversation event
      connection.on("RemoveAdHocFromConversation", handleRemoveAdHocFromConversation);

      return () => {
        connection.off("ReceiveTypingStatus", handleTypingStatus);
        connection.off("ReceiveMessage", handleReceiveMessage);
        connection.off("ReceiveReadReceipt", handleReceiveReadReceipt);
        connection.off("ConversationCreated", handleConversationCreated);
        connection.off("RemoveAdHocFromConversation", handleRemoveAdHocFromConversation);
      };
    }, [connection, userId]);

    // join groups when conversations change
    useEffect(() => {
      if (!connection) return;
      const conversationsToJoin = useUnifiedEndpoint ? unifiedConversations : conversations;
      conversationsToJoin.forEach((conv) => {
        connection
          .invoke("JoinConversation", conv.id)
          .catch((err) => console.error(`Failed to join conversation ${conv.id}`, err));
      });
    }, [connection, conversations, unifiedConversations, useUnifiedEndpoint]);

    // ----- CREATE NEW CONVERSATION -----
    const handleUserSelection = (selectedOptions: any) => {
      setSelectedUsers(selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []);
    };

    const handleCreateConversation = async () => {
      if (!selectedUsers.length) {
        alert("Please select at least 1 user");
        return;
      }

      try {
        const conResponse = await createConversation(authToken, selectedUsers);
        if (conResponse.success) {
          setAddConversationView(false);
          onSelectConversation(conResponse.data);
        } else {
          if (conResponse?.data?.id) {
            setSelectedUsers([]);
            toggleAddConversationView();
            navigate(`/chat/${conResponse.data.id}`)
          } else {
            toast.error(conResponse.message);
          }


        }
      } catch (err: any) {
        console.error("Failed to create conversation:", err);
        toast.error(err.message);
      }
    };

    const toggleAddConversationView = (anchorEl?: HTMLElement | null) => {
      setAddConversationView((prev) => {
        const isOpening = !prev;
        if (isOpening) {
          const targetEl = anchorEl || internalAddButtonRef.current;
          if (targetEl) {
            const rect = targetEl.getBoundingClientRect();
            setPopupStyle({
              position: "fixed",
              top: `${rect.bottom + 5}px`,
              left: `${rect.right}px`,
              transform: "translateX(-100%)",
            });
          } else {
            setPopupStyle({
              position: "absolute",
              top: "60px",
              right: "20px",
            });
          }
        }
        return isOpening;
      });
    };

    React.useImperativeHandle(ref, () => ({
      toggleAddConversation: (anchorEl?: HTMLElement | null) => {
        toggleAddConversationView(anchorEl);
      },
    }));

    // ----- RENDER -----
    // if (loading) return <div>Loading conversations...</div>;
    if (error) return <div>Error: {error}</div>;

    // Use unified endpoint if enabled, otherwise use legacy separate lists
    // let list: Conversation[] = [];
    // if (useUnifiedEndpoint) {
    //   // Filter unified list based on active tab and isChannel flag
    //   list = unifiedConversations.filter((conv) => {
    //     if (activeTab === "Conversations") {
    //       return !conv.isChannel; // Show only non-channels
    //     } else {
    //       return conv.isChannel === true; // Show only channels
    //     }
    //   });
    // } else {
    //   // Legacy behavior: use separate lists
    //   list = activeTab === "Conversations" ? conversations : channelConversations;
    // }

    let list: Conversation[] = [];

    if (useUnifiedEndpoint) {
      // No conditions — render everything
      list = unifiedConversations;
    } else {
      // Legacy — merge both
      list = [...conversations, ...channelConversations];
    }


    // filtered the conversation on the basis of searchTerm
    const filteredConversations = list.filter((conv) => {
      return conv.participants.some((participant) => {
        if (participant.id === userId) return false;
        const name = `${participant.displayName || ""} ${participant.firstName || ""} ${participant.lastName || ""
          }`.toLowerCase();
        return name.includes(searchTerm.toLowerCase());
      });
    });

    const onClickDeleteConversation = async (conId: number, token: string) => {
      const isConfirm = await showConfirmationDialog();
      if (!isConfirm) return;
      try {
        deleteConversationAsync(token, conId).then((res) => {
          if (res.success) {
            if (useUnifiedEndpoint) {
              getAllUnifiedConversations(token, showArchived);
            } else {
              getMyConversation(token);
            }
          } else {
            toast.error(res.message);
          }
        });
      } catch (error: any) {
        toast.error(error.message);
      }
    };

    // Optimistically mark read on selection (instant UI)
    const handleSelectConversation = (conv: Conversation) => {
      // Local state: unread -> 0
      setConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0, lastReadAt: new Date() } : c))
      );
      setChannelConversations((prev) =>
        prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0, lastReadAt: new Date() } : c))
      );

      if (useUnifiedEndpoint) {
        setUnifiedConversations((prev) =>
          prev.map((c) => (c.id === conv.id ? { ...c, unreadCount: 0, lastReadAt: new Date() } : c))
        );
      }
      // Publish to anyone else listening
      conversationRead$.next(conv.id);
      // Upstream select
      onSelectConversation(conv);
    };

    const formatTime = (date?: Date | string) => {
      if (!date) return "";

      return new Date(date).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    return (
      <>
        <div className="common_card myconversations_card p-0 h-100"
          style={{
            borderRadius: "15px 0px 0px 15px",
            borderRight: "#E5E7EB 1.5px solid",
            borderTop: "none",
            borderLeft: "none",
            borderBottom: "none",
          }}
        >
          {/* {showHeader && (
            <div className="myconversations_head">
              <div className="top border_tittle top_bar_flx">
                <div className="top_bar_tittle">
                  <Tab.Container id="left-tabs-example" defaultActiveKey="Conversations">
                    <div className="tab_flx flex-wrap">
                      <Nav
                        variant="pills"
                        className="common_tab"
                        activeKey={activeTab}
                        onSelect={(selectedKey) =>
                          setActiveTab(
                            (selectedKey as "Conversations" | "Channels") || "Conversations"
                          )
                        }
                      >
                        <Nav.Item>
                          <Nav.Link eventKey="Conversations">My Conversation</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="Channels">Channels</Nav.Link>
                        </Nav.Item>
                      </Nav>
                    </div>
                  </Tab.Container>
                </div>
                {activeTab === "Conversations" && (
                  <div className="relative flex items-center align-middle gap-2 cursor-pointer">
                     <div
                      ref={internalAddButtonRef}
                      className="flex items-center align-middle gap-1"
                      onClick={() => toggleAddConversationView()}
                    >
                      <div className="white_circle_icon white_circle_icon_sm">
                        <img src={plusicon} alt="plusicon" />
                      </div>
                    </div> 
                  </div>
                )}
              </div>
            </div>
          )} */}
          {/* {addConversationView && (
            <div
              className="select_user_dropdown"
              style={{
                ...popupStyle,
                zIndex: 1050,
                width: "300px",
              }}
            >
              <Form.Group className="custom_input_flled" controlId="">
                <Form.Label>Select User</Form.Label>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isMulti
                  options={users.map((user) => ({
                    value: user.id,
                    label: user.displayName || `${user.firstName} ${user.lastName}`,
                  }))}
                  value={selectedUsers.map((uid) => ({
                    value: uid,
                    label: users.find((u) => u.id === uid)?.displayName || uid,
                  }))}
                  onChange={handleUserSelection}
                  placeholder="Select users..."
                />
              </Form.Group>
              <div className="form_btn_flx">
                <Button variant="primary" onClick={handleCreateConversation} className="btn_sm">
                  Create
                </Button>
              </div>
            </div>
          )} */}

          {addConversationView && (
            <CreateConversationModal
              addConversationView={addConversationView}
              users={users}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              handleCreateConversation={handleCreateConversation}
              onClose={() => setAddConversationView(false)}
              chatClose={() => {
                onCloseSideChat?.();
              }}
            />
          )}
          {/* <Form.Group
            style={{ marginLeft: "1.25rem", marginRight: "1.25rem" }}
            controlId="searchInput"
            className="custom_input_flled"
          >
            <Form.Control
              type="search"
              placeholder="Search..."
              onChange={(e) => setSearchTearm(e.target.value)}
            />
          </Form.Group> */}

          {!isSideView && (
            <div className="pb-[30px] border-b pt-[15px]">
              <div className="flex items-center bg-[#F9FAFB] py-[3px] rounded-[10px] px-[12px] mt-[18px] w-[92%] mx-auto ">
                <Search className=" text-[#99A1AF] " />
                <input
                  type="search"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTearm(e.target.value)}
                  style={{
                    width: "100%",
                    border: "none",
                    background: "transparent",
                    outline: "none",
                    boxShadow: "none",
                    color: "#0A0A0A80",
                    fontWeight: "400",
                  }}

                />
              </div>
            </div>)}

          <div className="flex justify-between mt-[20px] px-[20px]">
            <p className="text-xl">All inbox</p>
            <div
              className={`bg-[#F4F4F4] w-8 h-8 rounded-full flex justify-center items-center
    ${!handleConversation ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
  `}
              onClick={() => handleConversation?.()} // ✅ SAFE
            >
              <Plus className="text-[23px] text-black" />
            </div>
          </div>

          {/* Archived channels toggle - only show for Channels tab when using unified endpoint */}
          {useUnifiedEndpoint && activeTab === "Channels" && (
            <div className="px-4 py-2 border-bottom">
              <Form.Check
                type="switch"
                id="show-archived-switch"
                label="Show archived channels"
                className='my_toggle mb-0'
                checked={showArchived}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setShowArchived(newValue);
                  // if (authToken) {
                  //   getAllUnifiedConversations(authToken, newValue);
                  // }
                }}
              />
              {archivedPermissionError && (
                <div className="text-danger small mt-2">
                  {archivedPermissionError}
                </div>
              )}
            </div>
          )}

          <ul className="my_conversations_list no-scrollbar"
            style={{
              paddingLeft: "0px",
              paddingRight: "0px",

            }}>
            {(loading || loadingUnified) ? (
              <div className="text-center py-4">
                <span>Loading conversations...</span>
                {/* Or use your spinner component here */}
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center">
                <p>No Conversation</p>
              </div>
            ) : (
              <>
                {filteredConversations.map((conv) => {
                  const isSelected = conv.id === selectedConversationId;
                  const style = { cursor: "pointer" as const };

                  /** Primary: unreadCount; Fallback: date compare; Always read if I'm author */
                  const lastMsg = conv.lastMessage as any | undefined;
                  const lastMsgTs =
                    (lastMsg && lastMsg._ts) ??
                    (lastMsg ? toTimestamp(lastMsg.createDate) : null);

                  const dateSaysRead =
                    !!lastMsgTs &&
                    !!conv.lastReadAt &&
                    new Date(conv.lastReadAt).getTime() >= lastMsgTs;

                  const authoredByMe = lastMsg?.author?.id === userId;

                  const read =
                    (typeof conv.unreadCount === "number"
                      ? conv.unreadCount === 0
                      : !!lastMsg && !!conv.lastReadAt && dateSaysRead) || authoredByMe;

                  // For group vs single participant
                  const participantsExcludingMe = conv.participants.filter((p) => p.id !== userId);
                  const isGroup = participantsExcludingMe.length > 1;
                  // Use isChannel flag to determine if this is a channel, fallback to channel object presence
                  const isChannelConversation = conv.isChannel === true || (conv.isChannel === undefined && !!conv.channel);
                  const conversationTitle = isChannelConversation && conv.channel?.name
                    ? conv.channel.name
                    : formatConversationTitle(participantsExcludingMe);

                  // Show last message text
                  let lastMsgText: React.ReactNode;
                  if (lastMsg) {
                    if (lastMsg.messageContent) {
                      var msg = conv.lastMessage?.messageType == MessageType.SYSTEM ? formatSystemMessage(lastMsg.messageContent) : lastMsg.messageContent;
                      lastMsgText =
                        msg.length > 100
                          ? msg.substring(0, 100) + "..."
                          : msg;

                    } else if (lastMsg.messageContent === "" || lastMsg.messageContent === null) {
                      lastMsgText = <em className="text-muted-light">File attachment</em>;
                    } else {
                      lastMsgText = "No messages yet";
                    }
                  } else {
                    lastMsgText = "No messages yet";
                  }

                  // Typing info
                  const typingUsers = typingMap[conv.id] || [];
                  const isSomeoneTyping = typingUsers.length > 0;

                  return (
                    <li
                      key={conv.id}
                      style={{
                        ...style,
                        paddingLeft: "15px",
                        paddingRight: "15px",
                      }}
                      onClick={() => handleSelectConversation(conv)}
                      className={`my_conversations_list_card ${isSelected ? "my_conversations_list_card_select" : "bg-transparent hover:bg-gray-100"
                        }`}

                    >
                      <div className="p-0.5 rounded-full"
                        style={{
                          border: "2px solid #6999FF"
                        }}
                      >
                        {isChannelConversation ? (
                          <div className="bg-[#f9f6ff] rounded-full p-1" style={{ border: "1px solid #E5E7EB" }}>
                            <img src={ChannelImage} alt="channel" className="w-[30px]" />
                          </div>
                        ) : isGroup ? (
                          <div className="my_conversations_list_card_img">
                            <img src={chatgroupicon} alt="chatgroupicon" />
                          </div>
                        ) : (
                          <AvatarImage userId={participantsExcludingMe[0]?.id} size="xs" />
                        )}
                      </div>
                      <div className="my_conversations_list_card_cntnt">
                        <div className="my_conversations_list_card_cntnt_left">
                          <div className="flex justify-between ">
                            <h3>{conversationTitle}</h3>
                            <p className="shrink-0" style={{
                              color: "#99A1AF",
                              fontSize: "11px"
                            }}>{formatTime(conv?.lastReadAt)}</p>
                          </div>
                          {isSomeoneTyping ? (
                            <div className="text-purple-500 italic">
                              {typingUsers.length > 1
                                ? "Several people are typing..."
                                : `${conv.participants.find((p) => p.id === typingUsers[0])?.displayName ||
                                "Someone"
                                } is typing...`}
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 me-1">
                              {lastMsg?.author?.id === userId && (
                                <FontAwesomeIcon className="text-xs text-muted-light" icon={faReply} />
                              )}
                              <p
                                className={`overflow-ellipsis line-clamp-1 ${!lastMsg || read ? "text-muted-light" : "font-bold text-purple-500"
                                  }`}
                                style={{ wordBreak: "break-word" }}
                              >
                                {lastMsgText}
                              </p>
                            </div>
                          )}
                        </div>
                        {!read && lastMsg && (
                          <div className="h-2 w-2 bg-blue-600 rounded-full text-ellipsis line-clamp-1"></div>
                        )}
                        {/* Right-side menu omitted */}
                      </div>
                    </li>
                  );
                })}
              </>
            )}
          </ul>
        </div>
      </>
    );
  }
);

export default ConversationList;
